import { ClockIcon, DotsIcon, DownloadIcon, ManualJournalIcon, PrintIcon, ReloadIcon } from '@approvalmax/ui';
import { Button, Divider, Dropdown, Flex, Menu, Text } from '@approvalmax/ui/src/components';
import { useSelector } from 'modules/react-redux';
import { memo } from 'react';
import { maxReportNameLength } from 'shared/constants';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { getActiveReportConfig } from '../../selectors/pageSelectors';
import { DropdownFieldEditor } from '../DropdownFieldEditor/DropdownFieldEditor';
import {
    useAccrualManualJournalPopup,
    useDiscardChanges,
    useDownloadAttachmentsArchive,
    useDownloadAuditReports,
    useReportToolbar,
    useReportToolbarDivider,
    useSaveAsNewReport,
    useSaveChanges,
    useScheduleReportPopup,
    useXeroAccrualReportPopup,
} from './ReportToolbar.hooks';
import { messages } from './ReportToolbar.messages';

export const ReportToolbar = memo(() => {
    const company = useCompanyBasedOnUrl();
    const reportConfig = useSelector(getActiveReportConfig)!;

    const { handlePrintReport, handleExportAsCsv, handleRemoveReport, handleRefreshReport, handleSaveReportName } =
        useReportToolbar(reportConfig);

    const actionSaveChanges = useSaveChanges(reportConfig);
    const actionSaveAsNewReport = useSaveAsNewReport(reportConfig);
    const actionDiscardChanges = useDiscardChanges();

    const popupScheduleReport = useScheduleReportPopup();
    const downloadAuditReport = useDownloadAuditReports(reportConfig);
    const popupXeroAccrualReport = useXeroAccrualReportPopup(reportConfig);
    const downloadAttachmentsArchive = useDownloadAttachmentsArchive(reportConfig);
    const popupAccrualManualJournal = useAccrualManualJournalPopup(
        reportConfig,
        company,
        actionSaveChanges.saveChanges
    );

    const isDividerAvailable = useReportToolbarDivider(reportConfig, company);

    return (
        <Flex spacing='16' alignItems='center' data-qa='report-toolbar'>
            <Flex grow={1} shrink={1} spacing='16' alignItems='center'>
                <Text font='label' data-qa='report-name'>
                    {reportConfig.name}
                </Text>

                <DropdownFieldEditor
                    textFieldLabel={messages.titleEditorLabel}
                    textFieldPlaceholder={messages.titleEditorPlaceholder}
                    textButtonActivator={messages.titleEditorActivator}
                    textButtonSubmit={messages.titleEditorSubmit}
                    textMaxLength={maxReportNameLength}
                    value={reportConfig.name}
                    onSubmit={handleSaveReportName}
                />
            </Flex>

            <Flex grow={0} shrink={0} spacing='12'>
                {popupAccrualManualJournal.isAvailable && (
                    <Button
                        icon
                        noPadding
                        title={messages.createAccrualsJournal}
                        disabled={company.flags.isExpired}
                        onClick={popupAccrualManualJournal.handleOpen}
                        variant='text'
                        color='blue100'
                    >
                        <ManualJournalIcon size={24} />
                    </Button>
                )}

                <Button
                    icon
                    noPadding
                    disabled={popupScheduleReport.isVisible}
                    onClick={popupScheduleReport.handleOpen}
                    title={messages.reportSchedulerTitle}
                    variant='text'
                    color='blue100'
                >
                    <ClockIcon size={24} />
                </Button>

                <Button
                    icon
                    noPadding
                    onClick={handlePrintReport}
                    title={messages.printReportTitle}
                    variant='text'
                    color='blue100'
                >
                    <PrintIcon size={24} />
                </Button>

                <Button
                    icon
                    noPadding
                    onClick={handleExportAsCsv}
                    title={messages.exportAsCsvTitle}
                    variant='text'
                    color='blue100'
                >
                    <DownloadIcon size={24} />
                </Button>

                <Button
                    icon
                    noPadding
                    onClick={handleRefreshReport}
                    title={messages.refreshReportDataTitle}
                    variant='text'
                    color='blue100'
                >
                    <ReloadIcon size={24} />
                </Button>

                <Dropdown
                    width={342}
                    closeOnClickInnerButton
                    activator={
                        <Button icon noPadding title={messages.otherActionsTitle} variant='text' color='blue100'>
                            <DotsIcon size={24} />
                        </Button>
                    }
                >
                    <Menu size='small' qa='report-toolbar'>
                        {actionSaveAsNewReport.isAvailable && (
                            <Menu.Item
                                name={messages.saveAsNewReportText}
                                onClick={actionSaveAsNewReport.handleSaveAsNewReport}
                                disabled={actionSaveAsNewReport.isDisabled}
                            />
                        )}

                        <Menu.Item
                            name={messages.discardChangesText}
                            onClick={actionDiscardChanges.handleDiscardChanges}
                            disabled={actionDiscardChanges.isDisabled}
                        />

                        <Menu.Item name={messages.removeReportText} onClick={handleRemoveReport} />

                        {isDividerAvailable && <Divider />}

                        {downloadAuditReport.isAvailable && (
                            <Menu.Item
                                name={messages.downloadAuditReportsText}
                                onClick={downloadAuditReport.handleDownload}
                            />
                        )}

                        {downloadAttachmentsArchive.isAvailable && (
                            <Menu.Item
                                name={messages.downloadAttachmentArchiveText}
                                onClick={downloadAttachmentsArchive.handleDownload}
                            />
                        )}

                        {popupXeroAccrualReport.isAvailable && (
                            <Menu.Item
                                name={messages.createAccrualReportXeroText}
                                disabled={popupXeroAccrualReport.isDisabled}
                                onClick={popupXeroAccrualReport.handleOpen}
                            />
                        )}
                    </Menu>
                </Dropdown>

                <Button
                    size='small'
                    color='blue80'
                    onClick={actionSaveChanges.saveChanges}
                    disabled={actionSaveChanges.isDisabled}
                >
                    {messages.saveButtonText}
                </Button>
            </Flex>
        </Flex>
    );
});

ReportToolbar.displayName = 'ReportsToolbar';
